/**
 * One panel, alone in a window.
 *
 * The desktop shell opens `index.html#/pane/<kind>`, and this renders that
 * panel with no rail, no switcher and no chrome. Same bundle, same session,
 * same theme, same formatters as the main window — which is the point of doing
 * it as a route rather than as a second application.
 *
 * ## Only panels that can actually stand alone are listed here
 *
 * A panel that takes its data as props needs the main window's data layer, and
 * a window rendering an empty shell is worse than a detach button that is not
 * offered. So this map holds the panels that fetch their own data, and
 * `DETACHABLE` in `panes.rs` holds exactly the same set — `panes.test.ts`
 * fails if the two drift. The rest become detachable when their data loading
 * is extracted from `App`, not before.
 *
 * ## It is the same device
 *
 * Nothing here registers a second device or opens a second upstream session.
 * Two windows of one application on one machine are one device — CME's unit of
 * count — and each panel's polling is another client of the same fan-out. See
 * `panes.rs` and ADR-0007.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api } from '../lib/api.js';
import { OrderBook } from './OrderBook.js';
import { Tape } from './Tape.js';
import { Footprint } from './Footprint.js';
import { ProfilePanel } from './ProfilePanel.js';
import { SymbolNews } from './SymbolNews.js';
import { PortfolioRiskPanel } from './PortfolioRisk.js';

export function PaneWindow({
  kind, symbol,
}: {
  /** Derived from the pane id in the hash — `chart:2` renders a chart. */
  kind: string;
  /** Passed at open time, never read from the main window later — a pane that
   *  silently followed whatever was last charted would change what it is about
   *  without saying so. */
  symbol: string | null;
}): JSX.Element {
  const precision = usePrecision(symbol);

  return (
    <main className="pane-window">
      {renderPane(kind, symbol, precision)}
    </main>
  );
}

function renderPane(
  kind: string, symbol: string | null, precision: number,
): JSX.Element {
  switch (kind) {
    case 'book':
      return symbol
        ? <OrderBook symbol={symbol} precision={precision} live />
        : <NoSymbol panel="The order book" />;
    case 'tape':
      return symbol
        ? <Tape symbol={symbol} precision={precision} live limit={120} />
        : <NoSymbol panel="Time and sales" />;
    case 'footprint':
      return symbol
        ? <Footprint symbol={symbol} live />
        : <NoSymbol panel="The footprint" />;
    case 'profile':
      return symbol
        ? <ProfilePanel symbol={symbol} />
        : <NoSymbol panel="The market profile" />;
    case 'news':
      return symbol
        ? <SymbolNews symbol={symbol} limit={20} />
        : <NoSymbol panel="News" />;
    case 'portfolio':
      return <PortfolioRiskPanel />;
    default:
      // A kind this build does not know. Named rather than blank, because the
      // trader asked for a specific panel and an empty window says nothing
      // about which of the two sides is out of date.
      return (
        <aside className="panel">
          <p className="panel-empty">
            This build has no panel called “{kind}”.
          </p>
        </aside>
      );
  }
}

function NoSymbol({ panel }: { panel: string }): JSX.Element {
  return (
    <aside className="panel">
      <p className="panel-empty">
        {panel} is about one instrument, and this window was opened without one.
        Close it and detach the panel again from the main window.
      </p>
    </aside>
  );
}

/**
 * Display precision for the pane's symbol, from the instrument master.
 *
 * Fetched rather than guessed. A price rendered at the wrong precision on a
 * ladder is not cosmetic — it merges two tradeable levels into one row, or
 * shows a digit the contract does not quote.
 */
function usePrecision(symbol: string | null): number {
  const [precision, setPrecision] = useState(2);

  useEffect(() => {
    if (!symbol) return undefined;
    let live = true;
    void api.instruments()
      .then((r) => {
        const found = r.instruments.find((i) => i.root === symbol);
        if (live && found) setPrecision(found.displayPrecision);
      })
      .catch(() => { /* Two decimals is the common case; a failed lookup is
                        not worth blanking the panel the window was opened for. */ });
    return () => { live = false; };
  }, [symbol]);

  return precision;
}
